/**
 * Dar e tirar cargos a partir do cartão que abre ao clicar num nome.
 *
 * Antes disto, atribuir um cargo obrigava a: abrir as definições do servidor,
 * encontrar a pessoa numa lista de membros, expandi-la e só então marcar a
 * caixa. Quatro passos e um sítio que ninguém visita — enquanto o gesto natural
 * («clico na pessoa e digo o que ela é») estava ali à mão e não fazia nada.
 *
 * A lista das definições NÃO desaparece: continua a ser onde se vê toda a gente
 * de uma vez, e onde se expulsa e bane. Isto é o atalho para o caso comum.
 *
 * **As regras são as mesmas do servidor, não umas mais frouxas.** O que se
 * mostra aqui é o que o `PATCH /servers/:id/members/:id` vai aceitar:
 *   · é preciso `ASSIGN_ROLES`;
 *   · não se mexe em quem está acima de nós nem no dono (`canActOn`);
 *   · não se dá um cargo acima do nosso próprio topo — senão qualquer moderador
 *     se promovia a dono;
 *   · a `@everyone` é automática e não se atribui.
 * Duplicar a regra no cliente não a torna a autoridade — o servidor recusa na
 * mesma. Serve para não mostrar um botão que só falha depois de carregado.
 */

import { canActOn, highestRank, Permission, type PublicUser, type Role, type Snowflake } from "@greenvoice/shared";
import { useState } from "react";

import { api } from "../lib/api";
import { can, selfPermissions } from "../store/selectors";
import { memberKey, useStore } from "../store/store";

export function UserCardRoles({ user, onClose }: { user: PublicUser; onClose?: () => void }) {
  const serverId = useStore((s) => s.activeServerId);
  const estado = useStore((s) => s);
  const abrirDefinicoes = useStore((s) => s.openServerSettings);
  const [aGravar, setAGravar] = useState<Snowflake | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  if (!serverId) return null;
  const self = estado.self;
  const servidor = estado.servers[serverId];
  if (!self || !servidor) return null;

  const alvo = estado.members[memberKey(serverId, user.id)];
  if (!alvo) return null; // não é membro deste servidor: não há cargos a dar

  if (!can(selfPermissions(estado, serverId), Permission.ASSIGN_ROLES)) return null;

  const daCasa = Object.values(estado.roles).filter((r) => r.serverId === serverId && !r.isDefault);
  // O `highestRank` só olha para o `rank`, mas o tipo pede o bitfield — e no
  // cliente ele vem serializado em texto. Passa-se zero: não é usado, e
  // desserializar aqui seria trabalho para nada.
  const rankDe = (ids: readonly Snowflake[]): number =>
    highestRank(
      ids
        .map((id) => estado.roles[id])
        .filter((r): r is Role => r !== undefined)
        .map((r) => ({ id: r.id, rank: r.rank, permissions: 0n })),
    );

  const euSouDono = servidor.ownerId === self.id;
  const meuMembro = estado.members[memberKey(serverId, self.id)];
  const meuRank = euSouDono ? -1 : rankDe(meuMembro?.roleIds ?? []);

  // Posso mexer NESTA pessoa?
  const podeMexer = canActOn(
    { isOwner: euSouDono, highestRank: meuRank },
    { isOwner: servidor.ownerId === user.id, highestRank: rankDe(alvo.roleIds) },
  );
  if (!podeMexer) return null;

  // E quais cargos posso DAR: só os abaixo do meu topo (o dono dá todos).
  const atribuiveis = daCasa.filter((r) => euSouDono || r.rank > meuRank).sort((a, b) => a.rank - b.rank);
  if (atribuiveis.length === 0) return null;

  const idDoServidor = serverId;
  const cargosActuais = alvo.roleIds;

  async function alternar(role: Role) {
    if (aGravar) return;
    setAGravar(role.id);
    setErro(null);
    const tem = cargosActuais.includes(role.id);
    const roleIds = tem ? cargosActuais.filter((id) => id !== role.id) : [...cargosActuais, role.id];
    try {
      const actualizado = await api.servers.updateMember(idDoServidor, user.id, { roleIds });
      useStore.setState((s) => ({
        members: { ...s.members, [memberKey(idDoServidor, user.id)]: actualizado },
      }));
    } catch (caught) {
      setErro(caught instanceof Error ? caught.message : "Não foi possível guardar.");
    } finally {
      setAGravar(null);
    }
  }

  return (
    <div className="user-card__cargos">
      <span className="user-card__cargos-titulo">Cargos</span>
      <div className="user-card__cargos-lista">
        {atribuiveis.map((role) => {
          const tem = cargosActuais.includes(role.id);
          return (
            <button
              key={role.id}
              type="button"
              className={`cargo-chip${tem ? " is-on" : ""}`}
              onClick={() => void alternar(role)}
              disabled={aGravar !== null}
              aria-pressed={tem}
            >
              <span className="cargo-chip__ponto" style={{ background: role.color ?? "var(--text-soft)" }} />
              {role.name}
            </button>
          );
        })}
      </div>
      {/* A porta para o resto. O cartão é o atalho do caso comum — criar um
          cargo, mudar as permissões dele ou ver a lista toda continua a ser
          trabalho das definições, e daqui vai-se lá direito à aba certa. */}
      <button
        type="button"
        className="user-card__cargos-mais"
        onClick={() => {
          abrirDefinicoes("roles");
          onClose?.();
        }}
      >
        Gerir cargos do servidor
      </button>
      {erro ? (
        <p className="user-card__cargos-erro" role="alert">
          {erro}
        </p>
      ) : null}
    </div>
  );
}
